import { useEffect, useState } from "react";
import { Btn } from "@/components/Btn";

export function SaveViewDialog({
  isAdmin,
  defaultName,
  onCancel,
  onSubmit,
}: {
  isAdmin: boolean;
  defaultName: string;
  onCancel: () => void;
  onSubmit: (opts: {
    name: string;
    visibility: "private" | "shared";
    setDefault: boolean;
  }) => Promise<void>;
}) {
  const [name, setName] = useState(defaultName);
  const [visibility, setVisibility] = useState<"private" | "shared">("private");
  const [setDefault, setSetDefault] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit() {
    if (!name.trim()) {
      setError("Name is required.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      await onSubmit({ name: name.trim(), visibility, setDefault });
    } catch (e: unknown) {
      setError(
        (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "Could not save."
      );
      setSubmitting(false);
    }
  }

  // Shared-default requires admin; auto-clear the flag when user
  // switches away from admin-eligible combos.
  const canSetDefault = visibility === "private" || isAdmin;
  useEffect(() => {
    if (!canSetDefault && setDefault) setSetDefault(false);
  }, [canSetDefault, setDefault]);

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(14,18,32,0.45)",
        zIndex: 100,
        display: "grid",
        placeItems: "center",
        padding: 24,
      }}
      onClick={(e) => {
        if (e.target === e.currentTarget) onCancel();
      }}
    >
      <div
        className="card"
        style={{
          width: "100%",
          maxWidth: 440,
          padding: 20,
        }}
      >
        <h3 style={{ margin: 0, fontSize: 16 }}>Save dashboard view</h3>
        <p className="muted" style={{ fontSize: 12.5, marginTop: 6 }}>
          Give this layout a name so you can come back to it from any
          device.
        </p>
        <div className="stack stack-16" style={{ marginTop: 16 }}>
          <div className="field">
            <label className="label">Name</label>
            <input
              className="input"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="My daily view"
              autoFocus
            />
          </div>
          <div className="field">
            <label className="label">Visibility</label>
            <div style={{ display: "flex", gap: 12, marginTop: 4 }}>
              <label className="check">
                <input
                  type="radio"
                  checked={visibility === "private"}
                  onChange={() => setVisibility("private")}
                />
                <span>
                  <b style={{ display: "block" }}>Private</b>
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    Only you can see it.
                  </span>
                </span>
              </label>
              <label className="check">
                <input
                  type="radio"
                  checked={visibility === "shared"}
                  onChange={() => setVisibility("shared")}
                />
                <span>
                  <b style={{ display: "block" }}>Shared</b>
                  <span className="muted" style={{ fontSize: 11.5 }}>
                    Everyone in your organization can load it.
                  </span>
                </span>
              </label>
            </div>
          </div>
          <label className="check">
            <input
              type="checkbox"
              checked={setDefault}
              disabled={!canSetDefault}
              onChange={(e) => setSetDefault(e.target.checked)}
            />
            <span>
              <b style={{ display: "block" }}>Make default</b>
              <span className="muted" style={{ fontSize: 11.5 }}>
                {visibility === "shared"
                  ? "Admin-only — becomes the default view for everyone in the org (unless they set a private default of their own)."
                  : "This view loads automatically for you on next visit."}
              </span>
            </span>
          </label>
          {error && (
            <div style={{ color: "var(--c-neg-600)", fontSize: 13 }}>
              {error}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
            <Btn type="button" onClick={onCancel}>
              Cancel
            </Btn>
            <Btn
              variant="primary"
              type="button"
              onClick={() => void submit()}
              disabled={submitting}
            >
              {submitting ? "Saving…" : "Save"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
